import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import {
  FileText, Briefcase, BookOpen, Users, MessageSquare,
  ClipboardList, TrendingUp, Plus, RefreshCw, ArrowRight,
  CheckCircle2, AlertTriangle, Dot
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('auth_token') || '';

// ── Dark stat card ───────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, sub, iconBg, badge, link }) {
  const inner = (
    <div
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
      style={{
        background: '#0A1828',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: 10,
        padding: '16px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        cursor: link ? 'pointer' : 'default',
        transition: 'border-color .15s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: iconBg || 'rgba(22,184,174,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={15} color={iconBg ? '#fff' : '#16B8AE'} />
        </div>
        {badge > 0 && (
          <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 20, background: 'rgba(220,38,38,0.2)', color: '#F87171', letterSpacing: '.06em', textTransform: 'uppercase' }}>
            {badge} new
          </span>
        )}
      </div>
      <div style={{ fontSize: 30, fontWeight: 900, color: '#fff', letterSpacing: '-.07em', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.5)', letterSpacing: '-.01em' }}>{label}</div>
      {sub && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.28)' }}>{sub}</div>}
    </div>
  );
  return link ? <Link to={link} style={{ textDecoration: 'none' }}>{inner}</Link> : inner;
}

// ── Cream right-rail panel ───────────────────────────────────────────────────
function RightPanel({ stats, loading }) {
  const INTEGRATIONS = [
    { label: 'ImageKit CDN',  status: 'connected' },
    { label: 'MongoDB',       status: 'connected' },
    { label: 'Gemini AI',     status: 'connected' },
    { label: 'Stripe',        status: 'test' },
    { label: 'ElevenLabs',    status: 'mocked' },
  ];

  const STATUS_COLOR = { connected: '#16A34A', test: '#D97706', mocked: '#6B7280' };

  const DIVIDER = '1px solid rgba(255,255,255,0.07)';
  const LABEL_STYLE = { fontSize: 9, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.25)', marginBottom: 10 };

  return (
    <div style={{
      width: 228, minWidth: 228,
      background: '#07101F',
      borderLeft: '1px solid rgba(255,255,255,0.06)',
      overflowY: 'auto',
      padding: '16px 0',
      scrollbarWidth: 'none',
    }}>

      {/* Quick Actions */}
      <section style={{ padding: '0 16px 14px', borderBottom: DIVIDER }}>
        <div style={LABEL_STYLE}>Quick Actions</div>
        <Link to="/admin/settings" style={{ display: 'block', padding: '7px 12px', background: '#C8951A', borderRadius: 7, fontSize: 12, fontWeight: 700, color: '#fff', textDecoration: 'none', textAlign: 'center', marginBottom: 8 }}>
          Create Announcement
        </Link>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {[
            { label: 'Create Content Item', to: '/admin/content/new' },
            { label: 'Add Magazine Issue',  to: '/admin/magazine/new' },
            { label: 'Create First Round',  to: '/admin/first-round' },
            { label: 'Add Widget',          to: '/admin/widgets' },
            { label: 'Create Banner',       to: '/admin/banners' },
            { label: 'Review Job Listing',  to: '/admin/jobs' },
          ].map(({ label, to }) => (
            <Link key={label} to={to} style={{ fontSize: 12, fontWeight: 600, color: '#C8951A', textDecoration: 'none', padding: '3px 0', display: 'flex', alignItems: 'center', gap: 4 }}>
              <ArrowRight size={10} style={{ flexShrink: 0 }} /> {label}
            </Link>
          ))}
        </div>
      </section>

      {/* People / Institutions */}
      <section style={{ padding: '14px 16px', borderBottom: DIVIDER }}>
        <div style={LABEL_STYLE}>People / Institutions</div>
        {[
          { label: 'Exemplary Leaders',   to: '/admin/exemplary' },
          { label: 'Institutions / Orgs', to: '/admin/institutions' },
          { label: 'Focus Areas',         to: '/admin/focus-areas' },
        ].map(({ label, to }) => (
          <Link key={label} to={to} style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#C8951A', textDecoration: 'none', padding: '3px 0' }}>
            {label}
          </Link>
        ))}
      </section>

      {/* Publishing Status */}
      <section style={{ padding: '14px 16px', borderBottom: DIVIDER }}>
        <div style={LABEL_STYLE}>Publishing Status</div>
        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', margin: 0 }}>
          {loading ? '—' : (stats?.content_items > 0 ? `${stats.content_items} content items active` : 'No active publications')}
        </p>
      </section>

      {/* Integration Status */}
      <section style={{ padding: '14px 16px', borderBottom: DIVIDER }}>
        <div style={LABEL_STYLE}>Integration Status</div>
        {INTEGRATIONS.map(({ label, status }) => (
          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>{label}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, color: STATUS_COLOR[status] || '#6B7280' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: STATUS_COLOR[status], display: 'inline-block' }} />
              {status === 'connected' ? 'Connected' : status === 'test' ? 'Test' : 'Mocked'}
            </span>
          </div>
        ))}
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {['Publishing Status', 'Integration Status', 'System Alerts'].map(l => (
            <span key={l} style={{ fontSize: 10, fontWeight: 700, color: '#C8951A', cursor: 'pointer', opacity: 0.85 }}>{l}</span>
          ))}
        </div>
      </section>

      {/* Alerts */}
      <section style={{ padding: '14px 16px', borderBottom: DIVIDER }}>
        <div style={LABEL_STYLE}>Alerts</div>
        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', margin: 0 }}>No active alerts.</p>
      </section>

      {/* Announcement Bar + Sponsor shortcuts */}
      <section style={{ padding: '14px 16px' }}>
        <Link to="/admin/settings" style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#C8951A', textDecoration: 'none', padding: '3px 0' }}>
          Announcement Bar
        </Link>
        <Link to="/admin/sponsors" style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#C8951A', textDecoration: 'none', padding: '3px 0' }}>
          Add Sponsor
        </Link>
      </section>
    </div>
  );
}

// ── Dashboard toolbar actions ────────────────────────────────────────────────
function DashboardActions({ onRefresh }) {
  return (
    <>
      <button onClick={onRefresh}
        style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, cursor: 'pointer', color: 'rgba(255,255,255,0.6)', fontSize: 11, fontWeight: 600 }}>
        <RefreshCw size={11} /> Refresh
      </button>
      <Link to="/admin/contacts"
        style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', background: '#C8951A', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#fff', fontSize: 11, fontWeight: 700, textDecoration: 'none' }}>
        <Plus size={11} /> New Inquiry
      </Link>
    </>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStats = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error();
      setStats(await res.json());
    } catch {
      setStats({ content_items: 0, ch_active_jobs: 0, ch_pending_jobs: 0, magazine_issues: 0, newsletter_subscribers: 0, contacts_new: 0, leads_new: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem('auth_token')) { window.location.href = '/admin/login'; return; }
    loadStats();
  }, []);

  const now = new Date();
  const dateStr = now.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <AdminLayout
      title="Dashboard"
      actions={<DashboardActions onRefresh={loadStats} />}
    >
      {/* Inner layout: main content + right panel side by side */}
      <div style={{ display: 'flex', height: '100%', minHeight: 0 }}>

        {/* ── Main content ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 24px 48px' }}>

          {/* Page heading */}
          <div style={{ marginBottom: 22 }}>
            <h1
              data-testid="dashboard-title"
              style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: 22, fontWeight: 900, color: '#fff', letterSpacing: '-.06em', margin: '0 0 4px' }}
            >
              Command Dashboard
            </h1>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', margin: 0 }}>{dateStr}</p>
          </div>

          {/* Stat grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }} data-testid="stats-grid">
            <StatCard icon={FileText}      label="Content Items"      value={loading ? '—' : stats?.content_items}         sub="Editorial Content Hub"        iconBg="rgba(22,184,174,0.2)"   link="/admin/content" />
            <StatCard icon={Briefcase}     label="Active Jobs"        value={loading ? '—' : stats?.ch_active_jobs}        sub={`${stats?.ch_pending_jobs ?? 0} pending review`} iconBg="rgba(0,140,134,0.3)" link="/admin/jobs" />
            <StatCard icon={BookOpen}      label="Magazine Issues"    value={loading ? '—' : stats?.magazine_issues}       sub="Published + drafts"           iconBg="rgba(200,149,26,0.3)"  link="/admin/magazine" />
            <StatCard icon={Users}         label="Newsletter Subs"    value={loading ? '—' : stats?.newsletter_subscribers} sub="Active subscribers"           iconBg="rgba(79,70,229,0.3)"   link="/admin/newsletter" />
            <StatCard icon={MessageSquare} label="New Inquiries"      value={loading ? '—' : stats?.contacts_new}         sub="Contact form"                 iconBg="rgba(220,38,38,0.3)"   badge={stats?.contacts_new} link="/admin/contacts" />
            <StatCard icon={ClipboardList} label="New Leads"          value={loading ? '—' : stats?.leads_new}            sub="Subscribe form"               iconBg="rgba(124,58,237,0.3)"  badge={stats?.leads_new} link="/admin/leads" />
          </div>

          {/* Career Hub status */}
          <div style={{ background: '#0A1828', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '16px 18px', marginBottom: 16 }} data-testid="ch-status">
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.28)', marginBottom: 12 }}>Career Hub Status</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 14 }}>
              {[
                { label: 'Active Listings', val: loading ? '—' : stats?.ch_active_jobs,   color: '#16B8AE' },
                { label: 'Pending Review',  val: loading ? '—' : stats?.ch_pending_jobs,  color: '#C8951A' },
                { label: 'Listing Plans',   val: '4',                                     color: 'rgba(255,255,255,0.7)' },
              ].map(({ label, val, color }) => (
                <div key={label} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 26, fontWeight: 900, color, letterSpacing: '-.07em', lineHeight: 1 }}>{val}</div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', fontWeight: 600, marginTop: 4 }}>{label}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Link to="/admin/jobs"      style={{ flex: 1, textAlign: 'center', padding: '7px', background: '#16B8AE', color: '#07121f', borderRadius: 7, fontSize: 11, fontWeight: 800, textDecoration: 'none' }} data-testid="review-queue-btn">Review Queue</Link>
              <Link to="/admin/job-plans" style={{ flex: 1, textAlign: 'center', padding: '7px', background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)', borderRadius: 7, fontSize: 11, fontWeight: 600, textDecoration: 'none' }}>Manage Plans</Link>
            </div>
          </div>

          {/* Magazine status */}
          <div style={{ background: '#0A1828', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '16px 18px', marginBottom: 16 }}>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.28)', marginBottom: 10 }}>Magazine</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Opportunity Post Monthly</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 3 }}>{loading ? '—' : stats?.magazine_issues} issue{stats?.magazine_issues !== 1 ? 's' : ''} in system</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Link to="/admin/magazine"     style={{ padding: '6px 14px', background: '#16B8AE', color: '#07121f', borderRadius: 7, fontSize: 11, fontWeight: 800, textDecoration: 'none' }}>Manage Issues</Link>
                <Link to="/admin/magazine/new" style={{ padding: '6px 14px', background: 'rgba(22,184,174,0.12)', color: '#16B8AE', borderRadius: 7, fontSize: 11, fontWeight: 600, textDecoration: 'none' }}>+ New Issue</Link>
              </div>
            </div>
          </div>

          {/* Recent contact activity */}
          <div style={{ background: '#0A1828', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '16px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.28)' }}>Recent Contact Activity</div>
              <Link to="/admin/contacts" style={{ fontSize: 11, fontWeight: 700, color: '#C8951A', textDecoration: 'none' }}>View All →</Link>
            </div>
            <div style={{ textAlign: 'center', padding: '24px 0', color: 'rgba(255,255,255,0.2)', fontSize: 12 }}>
              {loading ? '—' : (stats?.contacts_new === 0 ? 'No recent activity recorded yet.' : `${stats.contacts_new} new contact${stats.contacts_new !== 1 ? 's' : ''} waiting`)}
            </div>
          </div>
        </div>

        {/* ── Right panel ── */}
        <RightPanel stats={stats} loading={loading} />
      </div>
    </AdminLayout>
  );
};
